import React, { useState, useEffect, useRef } from "react";
import { getIntroPricing, formatCents, discountLabel } from "../../utils/introOffer";

// The web funnel's OWN paywall pieces (plans, Monthly / Annual switch, the
// Pro + Family cards with the count-down price, fine print, review card,
// keyframes). Started as a copy of the website's paywall
// (components/TrialWall.js) and then tuned for the funnel: the switch and the
// cards are sized to fit one phone screen (`compact` / `dense`), the price
// rolls down from the regular price, the savings pill is computed from the
// prices, the weekly stars carry the Pixley stars icon. Nothing in here is
// used by the website, the app paywall or make.pixleyai.com, and nothing here
// imports from them: the funnel can change without touching anything else.

const FREDOKA = { fontFamily: "'Fredoka', sans-serif" };

// The plans the funnel sells. Pro only for now (Family is off the funnel
// since 2026-10-16; the website paywall still sells it). With one plan there
// is no "most popular" badge to show. No amounts live here: every price on
// the paywall comes from GET /subscription-plans, which reads Stripe.
export const PLANS = [
  {
    tier: "pro",
    name: "Pro",
    blurb: "Best for most families",
    starsPerWeek: 250,
    badge: null,
    features: ["Up to 3 min stories", "9:16 vertical + 16:9", "All languages"],
  },
];

// `prices`: { [tier]: { monthly: cents, annual: cents } } from the plans
// response (`plans[].price_cents`); a plan with either amount missing is not
// priceable and must not be rendered.
export const priceable = (prices, tier) => {
  const p = prices?.[tier];
  return !!p && Number.isFinite(p.monthly) && p.monthly > 0 && Number.isFinite(p.annual) && p.annual > 0;
};

const REVIEW = {
  quote: "My 5 year old asks for Pixley every night.",
  author: "Sarah, mom of two",
};

export const CONFETTI_COLORS = ["#0065F8", "#FF6B9A", "#FFC93C", "#37D67A", "#8A5CF6", "#00CAFF"];

// ── Small inline icons (SVG only — no emoji per design rules) ──

const GoldStar = ({ size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="#FFB300">
    <polygon points="12 2 15.1 8.3 22 9.3 17 14.1 18.2 21 12 17.8 5.8 21 7 14.1 2 9.3 8.9 8.3" />
  </svg>
);
const PencilIcon = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 20h4.5L19 9.5a2.1 2.1 0 0 0-3-3L5.5 17 4 20z" />
    <path d="M13.5 8l3 3" />
  </svg>
);
// Pay what you want: the price on the card is the thing you set, so it sits in
// a tappable dashed field with a pencil badge on its corner and a hint under
// it; the whole field opens the chooser. It pulses twice once the price has
// landed, so the eye finds it without a label shouting.
const PriceField = ({ action, children }) => (
  <button
    type="button"
    onClick={action.onClick}
    aria-haspopup="dialog"
    aria-label={action.label}
    data-testid="price-action"
    style={{
      position: "relative",
      display: "inline-flex", alignItems: "baseline", justifyContent: "center",
      maxWidth: "100%", boxSizing: "border-box",
      margin: "2px 0 0",
      padding: "9px 22px 8px 18px",
      borderRadius: 20,
      background: action.chosen ? "#EAF3FF" : "#F5FAFF",
      border: "2px dashed rgba(0,101,248,0.5)",
      cursor: "pointer",
      WebkitTapHighlightColor: "transparent",
      transition: "transform 100ms ease, background 200ms ease",
      animation: "tw-pill-in 0.5s cubic-bezier(0.22, 1, 0.36, 1) 0.9s both, tw-field-pulse 1.5s ease-out 1.3s 2",
    }}
    onMouseDown={(e) => { e.currentTarget.style.transform = "scale(0.98)"; }}
    onMouseUp={(e) => { e.currentTarget.style.transform = ""; }}
    onMouseLeave={(e) => { e.currentTarget.style.transform = ""; }}
  >
    {children}
    <span
      aria-hidden="true"
      style={{
        position: "absolute", top: -11, right: -11,
        width: 28, height: 28, borderRadius: "50%",
        display: "flex", alignItems: "center", justifyContent: "center",
        background: "linear-gradient(180deg, #0A7DFF 0%, #0065F8 100%)",
        color: "#FFFFFF",
        boxShadow: "0px 3px 0px #073B8E, 0 0 0 3px #FFFFFF",
      }}
    >
      <PencilIcon size={14} />
    </span>
  </button>
);
const PriceHint = ({ action }) => (
  <div style={{ ...FREDOKA, fontSize: 13, fontWeight: 700, color: "#0065F8", marginTop: 7, letterSpacing: "0.01em" }}>
    {action.hint}
  </div>
);
const CheckChip = () => (
  <span
    style={{
      width: 18, height: 18, borderRadius: 999, flex: "0 0 auto",
      background: "#E7F0FF", color: "#0065F8",
      display: "inline-flex", alignItems: "center", justifyContent: "center",
    }}
  >
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 12.5l5.5 5.5L20 6.5" />
    </svg>
  </span>
);

// ── Signature press-down button (SubscriptionModal pattern) ──

export const popButton = (primary) => ({
  ...FREDOKA,
  width: "100%",
  padding: primary ? "13px 0" : "12px 0",
  borderRadius: 14,
  fontSize: 15,
  fontWeight: 700,
  cursor: "pointer",
  color: primary ? "#FFFFFF" : "#0065F8",
  background: primary ? "linear-gradient(180deg, #0A7DFF 0%, #0065F8 100%)" : "#FFFFFF",
  border: primary ? "1px solid rgba(255,255,255,0.5)" : "2px solid rgba(0,101,248,0.22)",
  boxShadow: primary
    ? "inset 0 1px 0 rgba(255,255,255,0.4), 0px 4px 0px #073B8E"
    : "0px 3px 0px rgba(11,26,51,0.06)",
  transition: "transform 100ms ease, box-shadow 100ms ease",
});

export const pressHandlers = (primary) => ({
  onMouseDown: (e) => {
    e.currentTarget.style.transform = "translateY(3px)";
    e.currentTarget.style.boxShadow = "0px 0px 0px transparent";
  },
  onMouseUp: (e) => {
    e.currentTarget.style.transform = "";
    e.currentTarget.style.boxShadow = popButton(primary).boxShadow;
  },
  onMouseLeave: (e) => {
    e.currentTarget.style.transform = "";
    e.currentTarget.style.boxShadow = popButton(primary).boxShadow;
  },
});

// ── Trial transparency timeline ──
// Entrance is staggered: each step slides up, its chip pops with a spring,
// and the rail grows down to hand off to the next step. Keyframes live at
// the modal level (tw-*) so both screens can use them.

export const ReviewCard = () => (
  <div
    style={{
      background: "#FFFFFF",
      border: "2px solid rgba(11,26,51,0.06)", borderRadius: 18,
      boxShadow: "0px 3px 0px rgba(11,26,51,0.04)",
      padding: "10px 16px", display: "flex", alignItems: "center", gap: 12,
    }}
  >
    <div
      style={{
        width: 38, height: 38, borderRadius: "50%", flex: "0 0 auto",
        background: "linear-gradient(135deg, #FF6B9A 0%, #FF8FB3 100%)",
        boxShadow: "0 6px 14px rgba(255,107,154,0.35), inset 0 1px 0 rgba(255,255,255,0.4)",
        display: "flex", alignItems: "center", justifyContent: "center",
        ...FREDOKA, fontSize: 19, fontWeight: 700, color: "#FFFFFF",
      }}
    >
      S
    </div>
    <div style={{ textAlign: "left" }}>
      <div aria-label="5 out of 5 stars" style={{ display: "flex", gap: 2, marginBottom: 3 }}>
        {[0, 1, 2, 3, 4].map((i) => <GoldStar key={i} size={13} />)}
      </div>
      <p style={{ fontSize: 13.5, color: "#0B1A33", fontWeight: 600, margin: "0 0 1px", lineHeight: 1.4 }}>
        “{REVIEW.quote}”
      </p>
      <p style={{ fontSize: 12, color: "#8CA0BC", margin: 0 }}>{REVIEW.author}</p>
    </div>
  </div>
);


// ── The paywall's pieces ──

// ── the price that comes down ──
// The discounted price doesn't just appear: it starts at the regular price
// and rolls down to the offer, then lands with a small pop — on first paint,
// and again whenever Monthly / Annual flips. People who've asked for reduced
// motion get the final price at once.

const prefersReducedMotion = () =>
  typeof window !== "undefined" && !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
export const PRICE_COUNTDOWN_MS = 900;

export const CountdownPrice = ({ fromCents, toCents, duration = PRICE_COUNTDOWN_MS, style }) => {
  const instant = prefersReducedMotion() || typeof requestAnimationFrame !== "function";
  const [cents, setCents] = useState(() => (instant ? toCents : fromCents));
  const [landed, setLanded] = useState(instant);
  const frameRef = useRef(null);

  useEffect(() => {
    if (instant) {
      setCents(toCents);
      setLanded(true);
      return undefined;
    }
    setLanded(false);
    setCents(fromCents);
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      setCents(Math.round(fromCents + (toCents - fromCents) * easeOutCubic(t)));
      if (t < 1) {
        frameRef.current = requestAnimationFrame(tick);
      } else {
        setLanded(true);
      }
    };
    frameRef.current = requestAnimationFrame(tick);
    return () => {
      if (typeof cancelAnimationFrame === "function") cancelAnimationFrame(frameRef.current);
    };
  }, [fromCents, toCents, duration, instant]);

  return (
    <span
      style={{
        ...style,
        display: "inline-block",
        fontVariantNumeric: "tabular-nums",
        animation: landed && !instant ? "tw-price-land 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)" : undefined,
      }}
      aria-live="off"
    >
      {formatCents(cents)}
    </span>
  );
};

export const trialCtaFor = (trialConfig) => {
  const hasTrial = (trialConfig?.duration_days || 0) > 0;
  return hasTrial
    ? trialConfig.price_display
      ? `Start ${trialConfig.price_display} trial`
      : `Start free trial`
    : "Become a member";
};

// What annual billing saves against twelve monthly payments, from the plan
// prices Stripe reports (e.g. Pro 11.99/mo yearly vs 14.99 monthly → 20%),
// so the pill can never drift from what the cards charge.
// "SAVE 20%" / "SAVE 15%+": floored to a five, never overstated
export const annualSavingsLabel = (plans = PLANS, prices = {}) => {
  const labels = plans.filter((p) => priceable(prices, p.tier))
    .map((p) => discountLabel(prices[p.tier].monthly * 12, prices[p.tier].annual))
    .filter(Boolean);
  if (!labels.length) return null;
  const best = labels.map((l) => parseInt(l, 10)).reduce((a, b) => Math.max(a, b), 0);
  const label = labels.find((l) => parseInt(l, 10) === best);
  return labels.length > 1 && new Set(labels).size > 1 ? `SAVE UP TO ${label}` : `SAVE ${label}`;
};

export const BillingToggle = ({ billingPeriod, onChange, compact = false, plans = PLANS, prices = {} }) => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginBottom: compact ? 22 : 24 }}>
    <span style={{ ...FREDOKA, fontSize: 14, fontWeight: 700, whiteSpace: "nowrap", color: billingPeriod === "monthly" ? "#0B1A33" : "#8CA0BC" }}>
      Monthly
    </span>
    <button
      onClick={() => onChange(billingPeriod === "annual" ? "monthly" : "annual")}
      aria-label="Toggle billing period"
      aria-pressed={billingPeriod === "annual"}
      style={{
        position: "relative", width: 56, minWidth: 56, height: 30, borderRadius: 999,
        flexShrink: 0, padding: 0, margin: 0, boxSizing: "border-box",
        background: billingPeriod === "annual" ? "#0065F8" : "#D7E3F3",
        border: "none", cursor: "pointer", transition: "background 200ms ease",
        boxShadow: "inset 0 2px 4px rgba(11,26,51,0.1)",
      }}
    >
      <div
        style={{
          position: "absolute", top: 3, left: billingPeriod === "annual" ? 29 : 3,
          width: 24, height: 24, borderRadius: "50%", background: "#FFF",
          boxShadow: "0px 2px 4px rgba(11,26,51,0.2)", transition: "left 200ms ease",
        }}
      />
    </button>
    <span style={{ ...FREDOKA, fontSize: 14, fontWeight: 700, whiteSpace: "nowrap", color: billingPeriod === "annual" ? "#0B1A33" : "#8CA0BC" }}>
      Annual
    </span>
    <span
      style={{
        padding: "4px 10px", borderRadius: 999, fontSize: 10.5, fontWeight: 800,
        background: "#0065F8", color: "#FFF", boxShadow: "0px 2px 0px #073B8E",
        letterSpacing: "0.04em", whiteSpace: "nowrap",
      }}
    >
      {annualSavingsLabel(plans, prices)}
    </span>
  </div>
);

/**
 * The Pro / Family cards. `introOffer` is the site's intro-offer terms
 * (subscription-status `introOffer`, or /subscription-plans `intro_offer`);
 * `hasIntroOffer` says whether this visitor gets them. `onSelect(tier)` is
 * each card's button.
 */
export const PlanGrid = ({
  plans = PLANS,
  prices = {},
  billingPeriod,
  hasIntroOffer,
  introOffer,
  trialConfig,
  onSelect,
  isProcessing = false,
  // Tighter card padding and feature rows (the funnel, where the whole Pro
  // card has to fit one phone screen)
  dense = false,
  // Pay what you want (the funnel's `pwyw` arm, monthly): the price becomes
  // a tappable field that opens the price chooser —
  // { label, hint, onClick, chosen }. `label` names the control for
  // assistive tech, `hint` is the line under it, `chosen` marks that a
  // price has been named (the field reads as theirs rather than an invite).
  priceAction = null,
}) => {
  const trialCta = trialCtaFor(trialConfig);
  const planIntroOffer = (tier) => introOffer?.plans?.[tier] || null;
  const regularCentsFor = (plan) => prices[plan.tier]?.[billingPeriod];
  // A fixed first-period price at (or above) the regular price is no offer
  // at all: the parent who names the regular price in the pay-what-you-want
  // arm sees the plain price, not a "0% off"
  const atRegular = (tier) => {
    const first = planIntroOffer(tier)?.first_period_cents?.[billingPeriod];
    const regular = prices[tier]?.[billingPeriod];
    return Number.isFinite(first) && Number.isFinite(regular) && first >= regular;
  };
  const showsIntroOffer = (tier) => (
    hasIntroOffer
    && planIntroOffer(tier)?.enabled === true
    && !atRegular(tier)
  );
  // Stripe's amounts, as dollars per month for the pieces that speak that way
  const monthlyDollars = (plan) => (prices[plan.tier]?.monthly || 0) / 100;
  const annualMonthlyDollars = (plan) => (prices[plan.tier]?.annual || 0) / 1200;
  const introPricingFor = (plan) => getIntroPricing({
    monthlyPrice: monthlyDollars(plan),
    annualMonthlyPrice: annualMonthlyDollars(plan),
    billingPeriod,
    regularCents: regularCentsFor(plan),
    discountPercent: planIntroOffer(plan.tier)?.discount_percent || 45,
    // The funnel's fixed first-period price for this period, when it has one
    firstPeriodCents: planIntroOffer(plan.tier)?.first_period_cents?.[billingPeriod],
  });
  const planCta = (plan) => showsIntroOffer(plan.tier)
    ? `Start ${plan.name} — ${introPricingFor(plan).discountedPrice}`
    : atRegular(plan.tier)
      ? `Start ${plan.name} — ${formatCents(regularCentsFor(plan))}`
      : trialCta;

  return (
    <div
      style={{
        display: "grid",
        // One plan sits centred at card width; two or more share the row
        gridTemplateColumns: plans.length === 1 ? "minmax(0, 420px)" : "repeat(auto-fit, minmax(230px, 1fr))",
        justifyContent: "center",
        gap: 16,
      }}
    >
      {plans.filter((plan) => priceable(prices, plan.tier)).map((plan, idx) => (
        <div
          key={plan.tier}
          style={{
            position: "relative", display: "flex", flexDirection: "column",
            animation: `tw-step-in 0.55s cubic-bezier(0.22, 1, 0.36, 1) ${(0.2 + idx * 0.18).toFixed(2)}s both`,
          }}
        >
          {plan.badge && (
            <div style={{ position: "absolute", top: -11, left: "50%", transform: "translateX(-50%)", zIndex: 2 }}>
              <span
                style={{
                  padding: "5px 14px", borderRadius: 999, fontSize: 10, fontWeight: 800,
                  letterSpacing: "0.08em", textTransform: "uppercase", color: "#FFF",
                  background: "#0065F8", boxShadow: "0px 3px 0px #073B8E", whiteSpace: "nowrap",
                }}
              >
                {plan.badge}
              </span>
            </div>
          )}
          <div
            style={{
              flex: 1, borderRadius: 20, padding: dense ? "22px 16px 16px" : "28px 20px 22px",
              background: "#FFFFFF",
              border: plan.badge ? "2.5px solid #0065F8" : "2px solid rgba(11,26,51,0.08)",
              boxShadow: plan.badge
                ? "0px 4px 0px rgba(0,101,248,0.15), 0px 12px 32px rgba(0,101,248,0.10)"
                : "0px 2px 0px rgba(11,26,51,0.04)",
              display: "flex", flexDirection: "column",
            }}
          >
            <h3
              style={{
                ...FREDOKA, fontSize: 28, fontWeight: 700, color: "#0065F8",
                textAlign: "center", margin: 0, textTransform: "uppercase",
              }}
            >
              {plan.name}
            </h3>
            <p style={{ fontSize: 15, color: "#8CA0BC", textAlign: "center", margin: dense ? "2px 0 7px" : "3px 0 10px" }}>
              {plan.blurb}
            </p>
            <div style={{ textAlign: "center", marginBottom: 4 }}>
              {showsIntroOffer(plan.tier) ? (() => {
                const pricing = introPricingFor(plan);
                return (
                  <>
                    <div style={{ display: "inline-flex", alignItems: "center", padding: dense ? "4px 12px" : "6px 14px", borderRadius: 999, background: "#E8F2FF", color: "#0065F8", ...FREDOKA, fontSize: dense ? 14 : 15, fontWeight: 700, letterSpacing: "0.04em", marginBottom: dense ? 8 : 11 }}>
                      {/* from the two prices on the card, so it can never disagree with them */}
                      {(planIntroOffer(plan.tier)?.discount_label?.[billingPeriod] || discountLabel(pricing.regularCents, pricing.discountedCents))} OFF
                    </div>
                    {(() => {
                      const row = (
                        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "center", gap: billingPeriod === 'annual' ? 9 : 13, flexWrap: "nowrap", whiteSpace: "nowrap", width: priceAction ? undefined : "100%" }}>
                          <span style={{ ...FREDOKA, fontSize: billingPeriod === 'annual' ? 21 : 24, fontWeight: 700, color: "#8A99B3", lineHeight: 1, letterSpacing: "-0.02em", textDecoration: "line-through", textDecorationThickness: 2.5 }}>
                            {pricing.regularPrice}
                          </span>
                          <CountdownPrice
                            fromCents={pricing.regularCents}
                            toCents={pricing.discountedCents}
                            style={{ ...FREDOKA, fontSize: billingPeriod === 'annual' ? 34 : 40, fontWeight: 700, color: "#0B1A33", lineHeight: 1, letterSpacing: "-0.02em" }}
                          />
                        </div>
                      );
                      // on its own line: the field is inline-level and would otherwise sit beside the chip
                      return priceAction ? <div><PriceField action={priceAction}>{row}</PriceField></div> : row;
                    })()}
                    {priceAction && <PriceHint action={priceAction} />}
                    <div style={{ ...FREDOKA, fontSize: 13, fontWeight: 600, color: "#334155", marginTop: 4 }}>
                      for your {pricing.periodLabel}
                    </div>
                    <div style={{ fontSize: 11.5, color: "#5A6B85", marginTop: 4 }}>
                      Then {pricing.renewalPrice} · cancel anytime
                    </div>
                  </>
                );
              })() : (
                (() => {
                  const plain = (
                    <>
                      <span style={{ ...FREDOKA, fontSize: 36, fontWeight: 700, color: "#0B1A33", lineHeight: 1 }}>
                        ${(billingPeriod === "annual" ? annualMonthlyDollars(plan) : monthlyDollars(plan)).toFixed(2)}
                      </span>
                      <span style={{ fontSize: 12.5, color: "#8A99B3" }}>/mo</span>
                    </>
                  );
                  return priceAction ? (
                    <>
                      <div><PriceField action={priceAction}>{plain}</PriceField></div>
                      <PriceHint action={priceAction} />
                    </>
                  ) : plain;
                })()
              )}
            </div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 5, marginBottom: 8 }}>
              {/* The Pixley stars icon, as in the star shop and plan modals */}
              <img src="/assets/pixley_stars.png" alt="" aria-hidden="true" style={{ width: 16, height: 16, objectFit: "contain" }} />
              <span style={{ ...FREDOKA, fontSize: 15, fontWeight: 700, color: "#0065F8" }}>
                {plan.starsPerWeek}
              </span>
              <span style={{ fontSize: 11.5, color: "#334155" }}>stars every week</span>
            </div>
            <ul style={{ listStyle: "none", padding: 0, margin: dense ? "0 0 12px" : "0 0 16px", flex: 1 }}>
              {plan.features.map((f) => (
                <li key={f} style={{ fontSize: 13, color: "#334155", padding: dense ? "2.5px 0" : "4px 0", display: "flex", alignItems: "center", gap: 8 }}>
                  <CheckChip /> {f}
                </li>
              ))}
            </ul>
            <button
              onClick={() => onSelect(plan.tier)}
              disabled={isProcessing}
              style={{
                ...popButton(!!plan.badge),
                fontSize: 14,
                cursor: isProcessing ? "wait" : "pointer",
                opacity: isProcessing ? 0.7 : 1,
              }}
              {...pressHandlers(!!plan.badge)}
            >
              {planCta(plan)}
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

export const PaywallFootnote = ({ hasIntroOffer, hasTrial, trialConfig, billingPeriod }) => (
  <p style={{ fontSize: 11.5, color: "#8CA0BC", textAlign: "center", margin: "20px 20px 0" }}>
    {hasIntroOffer
      ? `The introductory price applies to your ${billingPeriod === "annual" ? "first year" : "first month"}, then renews at the regular price. Cancel anytime in Settings.`
      : hasTrial
      ? `After your ${trialConfig.duration_days}-day trial, your plan renews automatically. Cancel anytime in Settings.`
      : "Renews automatically. Cancel anytime in Settings."}
  </p>
);

// The keyframes the pieces above animate with. The modal inlines the same
// rules; anything else that renders the pieces includes this once.
export const PaywallKeyframes = () => (
  <style>{`
    @keyframes trialwall-confetti-fall {
      0% { top: -6%; transform: rotate(0deg); opacity: 1; }
      80% { opacity: 1; }
      100% { top: 104%; transform: rotate(420deg); opacity: 0; }
    }
    @keyframes tw-step-in {
      0% { opacity: 0; transform: translateY(12px); }
      100% { opacity: 1; transform: translateY(0); }
    }
    @keyframes tw-chip-pop {
      0% { transform: scale(0.3); }
      70% { transform: scale(1.18); }
      100% { transform: scale(1); }
    }
    @keyframes tw-rail-grow {
      0% { transform: scaleY(0); }
      100% { transform: scaleY(1); }
    }
    @keyframes tw-screen-in {
      0% { opacity: 0; transform: translateX(36px); }
      100% { opacity: 1; transform: translateX(0); }
    }
    @keyframes tw-price-land {
      0% { transform: scale(1); }
      45% { transform: scale(1.14); }
      100% { transform: scale(1); }
    }
    @keyframes tw-pill-in {
      0% { opacity: 0; transform: translateY(6px) scale(0.94); }
      60% { opacity: 1; transform: translateY(-1px) scale(1.03); }
      100% { opacity: 1; transform: translateY(0) scale(1); }
    }
    @keyframes tw-field-pulse {
      0% { box-shadow: 0 0 0 0 rgba(0,101,248,0.35); }
      70% { box-shadow: 0 0 0 12px rgba(0,101,248,0); }
      100% { box-shadow: 0 0 0 0 rgba(0,101,248,0); }
    }
  `}</style>
);

/** The confetti that falls through the paywall's hero band. */
export const paywallConfetti = (count = 14) => Array.from({ length: count }, (_, i) => ({
  left: `${(i * 41 + 7) % 100}%`,
  delay: `${(i % 7) * 0.5}s`,
  duration: `${3.2 + (i % 5) * 0.5}s`,
  color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
  size: 6 + (i % 3) * 3,
  rotate: (i * 53) % 360,
}));
